import { AnchorLink } from "gatsby-plugin-anchor-links";
import gsap from "gsap";
import React, { useRef, useLayoutEffect, ReactElement } from "react";
import { Box, Flex, Heading, Text } from "rebass";
import styled from "styled-components";
import Arrow from "../icons/Arrow";

interface IProps {
  title: string;
  titleRow?: string | undefined;
  titleClassName?: string | undefined;
  subtitle?: string | undefined;
  shortDesc?: ReactElement | null;
  role?: ReactElement | null;
  roleDesc?: ReactElement | null;
  disciplines?: ReactElement | null;
  challenge?: ReactElement | null;
  year?: string | undefined;
  img?: string | undefined;
}

const Hero = (props: IProps) => {
  let pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const pageUrl = pathname + "#project-content";
  const sectionUrl = pathname + "#my-contributon";
  const revealRefsHero = useRef([null]);
  revealRefsHero.current = [];
  useLayoutEffect(() => {
    revealRefsHero.current.forEach((el) => {
      gsap.fromTo(
        el,
        {
          autoAlpha: 0,
        },
        {
          duration: 1,
          autoAlpha: 1,
          delay: 0.1,
          ease: "Power4.easeOut",
          stagger: {
            each: 0.1,
            from: "center",
            grid: "auto",
            ease: "Power2.inOut",
          },
        }
      );
    });
  }, []);

  const addToRefs = (el: any) => {
    if (el && !revealRefsHero.current.includes(el)) {
      revealRefsHero.current.push(el);
    }
  };

  return (
    <StyledSection>
      <Flex
        flexWrap="wrap"
        justifyContent="center"
        variant="containerHero"
      >
        <Box width={[1 / 1]}>
          <Heading as="h6" variant="catHeading" ref={addToRefs}>
            Case name
          </Heading>
          <Flex flexWrap="wrap">
            <Box width={[1 / 1, 8 / 12]} variant="containerBigTitle">
              <Heading
                as="h2"
                className={"projectTitle " + props.titleClassName}
                variant="bigTitle"
              >
                <span ref={addToRefs}>{props.title}</span>
                <span ref={addToRefs}>{props.titleRow}</span>
              </Heading>
            </Box>
            <Box width={[1 / 1, 4 / 12]} pl={[0,5]} minHeight={["25vh","none"]}>
              360°
            </Box>
          </Flex>
        </Box>
        <Box width={[1 / 1]} textAlign="center" mb={[3, 4]} sx={{ position: "relative", zIndex: "99"}}>
          <StyledAnchorLink to={sectionUrl} title="My Contribution">
            <span>My contribution</span>
            <Arrow />
          </StyledAnchorLink>
        </Box>

        {/* Summary */}
        <Box variant="containerTxt" id="my-contributon">
          <Heading as="h6" variant="catHeading">
            Long Story short
          </Heading>
          <Text
            as="p"
            my={[2, 3]}
            fontWeight={"bold"}
          >
            {props.subtitle}
          </Text>
          <Text as="p" variant="paragraph" ref={addToRefs}>
            {props.shortDesc}
          </Text>

          <Flex flexWrap="wrap">
            {/* My Role */}
            <Box width={[1 / 2]}>
              <Heading as="h6" variant="catHeading">
                My Role
              </Heading>
              <Text
                as="p"
                fontWeight={"bold"}
                my={[2, 3]}
              >
                {props.role}
              </Text>
            </Box>
            <Box width={[1 / 2]}>
              {/* Year */}
              <Heading as="h6" variant="catHeading" textAlign="right">
                Year
              </Heading>
              <Text
                as="p"
                fontWeight={"bold"}
                mt={[2, 3]}
                textAlign="right"
              >
                {props.year}
              </Text>
            </Box>
          </Flex>
          {/* My Role Description */}
          <Text as="p" variant="paragraph" ref={addToRefs}>
            {props.roleDesc}
          </Text>

          {/* Disciplines */}
          <Heading as="h6" variant="catHeading">
            Disciplines Involved (A-Z)
          </Heading>
          <Text as="p" ref={addToRefs} mb={[3,4]} fontWeight={"bold"}>
            {props.disciplines}
          </Text>

          {/* Challenge */}
          <Heading as="h6" variant="catHeading">
            The Challenge
          </Heading>
          <Text as="p" variant="paragraph" ref={addToRefs}>
            {props.challenge}
          </Text>
        </Box>
        <Box width={[1 / 1]} textAlign="center">
          <StyledAnchorLink to={pageUrl} title="Project content">
            <span>Deep dive</span>
            <Arrow />
          </StyledAnchorLink>
        </Box>
      </Flex>

      <Box
        order={[1, 2]}
        width={[1 / 1]}
        sx={{ position: "relative" }}
        ref={addToRefs}
      >
      </Box>
    </StyledSection>
  );
};

const StyledSection = styled.section`
  color: inherit;
  width: 100vw;
  justify-content: center;
  display: flex;
  flex-direction: column;
  & .projectTitle {
    padding-bottom: 0;
    margin-bottom: 0;
    & > span {
      display: block;
    }
  }
`;

const StyledAnchorLink = styled(AnchorLink)`
  position:relative;
  z-index: 99;
  & > span {
    display: block;
    opacity: 0;
    transition: opacity 0.1s ease-in-out;
    padding: 0.4rem;
  }
  ,
  &:hover span {
    opacity: 1;
  }
  ,
  & > svg, & > div > svg {
    width: 3.2rem;
    height: auto;
    transform: rotate(90deg);
  }
`;

export default Hero;
